import { FormEvent, useEffect, useRef, useState } from "react";
import { Button, LockState, WatchingEmpty } from "@precyz/ui";
import { MapHost } from "./map/MapHost";
import { api, returnTo, type ApiError } from "./api";
import { PASSWORD_MIN_LENGTH, signupValidationMessage } from "./auth";
import { totpManualKey, totpQrDataUrl } from "./totp-qr";
import { CHROME_DOWNLOAD, offerInstallToken } from "./pairing";

export type Plan = "solo" | "pro" | "team" | "enterprise";

const labels: Record<string, string> = {
  analysis: "Historical analysis", posture: "Posture", devices: "Devices", alerts: "Alerts",
  reports: "Reports", privacy: "Privacy and transparency", org: "Organisation", audit: "Audit",
  integrations: "Integrations", developer: "Developer", billing: "Billing", settings: "Settings",
  assistant: "Assistant", search: "Search",
};

function notice(err: unknown): string {
  const apiErr = err as ApiError;
  if (apiErr?.detail) return apiErr.detail;
  return "The request could not be completed. Try again.";
}

function TotpQr({ uri, manualKey }: { uri: string; manualKey: string }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let cancelled = false;
    totpQrDataUrl(uri).then((url) => { if (!cancelled) setSrc(url); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [uri]);
  return <div className="totp-enroll">
    {src ? <figure className="totp-qr"><img alt="QR code for your Precyz authenticator key" src={src} width={220} height={220}/></figure> : null}
    <p className="otpauth"><a href={uri}>Open in authenticator</a></p>
    <details className="totp-manual">
      <summary>Can't scan? Enter this key manually</summary>
      <code className="manual-key">{manualKey || totpManualKey(uri)}</code>
    </details>
  </div>;
}

export function LiveScreen() {
  const ref = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [summary, setSummary] = useState({ event_count: 0, bytes_out: 0, bytes_in: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const host = new MapHost();
    host.mount(ref.current);
    return () => host.destroy();
  }, []);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      Promise.all([
        api<{ events: Array<Record<string, unknown>> }>("/v1/events").catch(() => ({ events: [] })),
        api<typeof summary>("/v1/aggregate/summary").catch(() => summary),
      ]).then(([events, next]) => {
        if (cancelled) return;
        setRows(events.events ?? []);
        setSummary(next);
      });
    };
    load();
    const id = setInterval(load, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return <>
    <section className="screen-title"><div><p className="eyebrow">LIVE</p><h1>Connection map</h1></div>
      <span className="quality">◇ Watching · {summary.event_count} events in the 120 s buffer</span></section>
    <div className="map-shell"><div ref={ref} className="map" aria-label="Live destination map"/>
      {rows.length === 0 ? <WatchingEmpty/> : null}</div>
    <StatsTable rows={rows}/>
  </>;
}

export function StatsTable({ rows = [] }: { rows?: Array<Record<string, unknown>> }) {
  const body = rows.length === 0
    ? <tr><td colSpan={8}>Watching where your connections go.</td></tr>
    : rows.slice(0, 30).map((row, i) => <tr key={String(row.EventID ?? row.event_id ?? i)}>
      <td>{String(row.EnrichedAt ?? row.t_start ?? "")}</td>
      <td>{String(row.OriginName ?? row.origin_name ?? "device")}</td>
      <td>{String(row.DstCountry ?? row.dst_country ?? "—")}</td>
      <td>{String(row.AsnOrg ?? "—")}</td>
      <td>{String(row.DstDomain ?? row.dst_domain ?? "held on paid plans")}</td>
      <td>{String(row.Proto ?? row.proto ?? "https")}</td>
      <td>{String(row.BytesOut ?? row.bytes_out ?? 0)}</td>
      <td>{String(row.BytesIn ?? row.bytes_in ?? 0)}</td>
    </tr>);
  return <section className="panel"><h2>Connection metadata</h2><div className="table-wrap"><table>
    <thead><tr>{["Time","Device","Destination country","Network operator","Domain","Protocol","Out","In"].map(h => <th key={h}>{h}</th>)}</tr></thead>
    <tbody>{body}</tbody></table></div></section>;
}

export function AuthScreen({ kind }: { kind: string }) {
  const params = new URLSearchParams(location.search);
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState(params.get("token") ?? "");
  const [message, setMessage] = useState("");
  const [otpauth, setOtpauth] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const intended = params.get("plan");
  if (intended) sessionStorage.setItem("precyz.plan_intent", intended);

  useEffect(() => {
    if (kind === "verify-email") {
      const id = setInterval(() => {
        api<{ state: string }>("/v1/auth/verify-status").then((data) => {
          if (data.state === "verified") location.assign("/signin/enroll");
        }).catch(() => undefined);
      }, 2500);
      return () => clearInterval(id);
    }
    if (kind === "signin/enroll") {
      api<{ otpauth_uri: string; manual_key?: string }>("/v1/auth/factors/totp", { method: "POST" })
        .then((data) => {
          setOtpauth(data.otpauth_uri);
          setManualKey(data.manual_key ?? totpManualKey(data.otpauth_uri));
        })
        .catch((err) => setMessage(notice(err)));
    }
    if (kind === "signout") {
      api("/v1/auth/session", { method: "DELETE" }).finally(() => location.replace("/signin"));
    }
    if (kind === "signup/recovery") {
      const stored = sessionStorage.getItem("precyz.recovery_codes");
      sessionStorage.removeItem("precyz.recovery_codes");
      if (stored) setCodes(JSON.parse(stored));
    }
    return undefined;
  }, [kind]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    const form = new FormData(event.currentTarget);
    const nextEmail = String(form.get("email") ?? email);
    const nextPassword = String(form.get("password") ?? password);
    const nextCode = String(form.get("code") ?? code);
    const nextToken = String(form.get("token") ?? token);
    try {
      if (kind === "signup") {
        const invalid = signupValidationMessage(nextEmail, nextPassword);
        if (invalid) { setMessage(invalid); return; }
        await api("/v1/auth/signup", { method: "POST", body: JSON.stringify({ email: nextEmail, password: nextPassword }) });
        location.assign("/verify-email");
        return;
      }
      if (kind === "signin") {
        const data = await api<{ next: string }>("/v1/auth/session", { method: "POST", body: JSON.stringify({ email: nextEmail, password: nextPassword }) });
        if (data.next === "verify_email") location.assign("/verify-email");
        else if (data.next === "totp") location.assign("/signin/2fa");
        else location.assign(params.get("return") || "/live");
        return;
      }
      if (kind === "signin/2fa") {
        await api("/v1/auth/factors/totp/verify", { method: "POST", body: JSON.stringify({ code: nextCode }) });
        location.assign(params.get("return") || "/live");
        return;
      }
      if (kind === "signin/enroll") {
        await api("/v1/auth/factors/totp/verify", { method: "POST", body: JSON.stringify({ code: nextCode }) });
        const issued = await api<{ codes: string[] }>("/v1/auth/recovery-codes", { method: "POST" });
        sessionStorage.setItem("precyz.recovery_codes", JSON.stringify(issued.codes ?? []));
        location.assign("/signup/recovery");
        return;
      }
      if (kind === "signin/recovery") {
        await api("/v1/auth/recovery-codes/consume", { method: "POST", body: JSON.stringify({ email: nextEmail, password: nextPassword, code: nextCode }) });
        location.assign("/live");
        return;
      }
      if (kind === "verify-email" && nextToken) {
        await api("/v1/auth/verify-email", { method: "POST", body: JSON.stringify({ token: nextToken }) });
        location.assign("/signin/enroll");
        return;
      }
      if (kind === "reset-password" && !nextToken) {
        await api("/v1/auth/password/reset", { method: "POST", body: JSON.stringify({ email: nextEmail }) });
        setMessage("If that address is enrolled, a reset mail is on its way.");
        return;
      }
      if (kind === "reset-password") {
        await api("/v1/auth/password/reset", { method: "POST", body: JSON.stringify({ email: nextEmail, token: nextToken, new_password: nextPassword }) });
        location.assign("/signin");
      }
    } catch (err) {
      setMessage(notice(err));
    }
  }

  const title: Record<string, string> = {
    signin: "Sign in to Precyz", signup: "Create your Precyz account",
    "verify-email": "Verify your email", "signin/2fa": "Enter your authentication code",
    "signin/recovery": "Use a recovery code", "reset-password": "Reset your password",
    "session-expired": "Your session expired", "signin/enroll": "Protect this account",
    "signup/recovery": "Store these recovery codes", signout: "Signing out",
  };

  if (kind === "signup/recovery") {
    return <main className="auth"><a href="https://precyz.com" className="brand">Precyz</a>
      <section className="auth-card"><h1>{title[kind]}</h1>
        <p>Download or copy these codes. Each code works once, and they replace TOTP when your authenticator is unavailable.</p>
        <pre className="codes">{codes.join("\n") || "Codes are shown only once at enrollment."}</pre>
        <div className="actions">
          <Button type="button" onClick={() => {
            const blob = new Blob([codes.join("\n")], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = "precyz-recovery-codes.txt"; a.click();
          }}>Download codes</Button>
          <Button type="button" variant="secondary" onClick={() => navigator.clipboard.writeText(codes.join("\n")).catch(() => undefined)}>Copy codes</Button>
          <a href="/live">Continue to live view</a>
        </div>
      </section></main>;
  }

  const showEmail = !kind.includes("2fa") && kind !== "signin/enroll" && kind !== "signout";
  const showPassword = kind === "signup" || kind === "signin" || kind === "signin/recovery" || (kind === "reset-password" && Boolean(token));
  const showCode = kind.includes("2fa") || kind === "signin/recovery" || kind === "signin/enroll";
  const showToken = kind === "verify-email" || kind === "reset-password";

  return <main className="auth"><a href="https://precyz.com" className="brand">Precyz</a>
    <section className="auth-card">
      <h1>{title[kind] ?? "Continue to Precyz"}</h1>
      {kind === "signup" ? <p>Personal is free forever for one user and up to three devices. Choose a password of at least {PASSWORD_MIN_LENGTH} characters, then enroll an authenticator app.</p> : kind === "signin/enroll" ? <p>Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, or any TOTP app), then enter the 6-digit code below.</p> : <p>Account access uses email, password, and a required second factor.</p>}
      {otpauth ? <TotpQr uri={otpauth} manualKey={manualKey}/> : null}
      {kind === "session-expired" ? <p><a href={returnTo(params.get("return") || "/live")}>Sign in to resume</a></p> : null}
      <form onSubmit={onSubmit}>
        {showEmail ? <label>Email<input name="email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} required={kind !== "verify-email"}/></label> : null}
        {showPassword ? <label>Password{kind === "signup" || (kind === "reset-password" && Boolean(token)) ? <span> At least {PASSWORD_MIN_LENGTH} characters</span> : null}<input name="password" type="password" autoComplete={kind === "signup" ? "new-password" : "current-password"} value={password} onChange={e => setPassword(e.target.value)} minLength={kind === "signup" || (kind === "reset-password" && Boolean(token)) ? PASSWORD_MIN_LENGTH : undefined} required/></label> : null}
        {showCode ? <label>Authentication code<input name="code" inputMode="numeric" autoComplete="one-time-code" placeholder={kind === "signin/enroll" || kind.includes("2fa") ? "000 000" : undefined} value={code} onChange={e => setCode(e.target.value)} required/></label> : null}
        {showToken ? <label>{kind === "verify-email" ? "Verification token" : "Reset token"}<input name="token" value={token} onChange={e => setToken(e.target.value)} autoComplete="off"/></label> : null}
        {kind !== "session-expired" && kind !== "signout" ? <Button type="submit">Continue</Button> : null}
      </form>
      {message ? <p role="status">{message}</p> : null}
      <p className="auth-links">
        {kind === "signin" ? <><a href="/signup">Create an account</a> · <a href="/signin/recovery">Use a recovery code</a> · <a href="/reset-password">Reset password</a></> : null}
        {kind === "signup" ? <a href="/signin">Already have an account</a> : null}
        {kind === "signin/2fa" ? <a href="/signin/recovery">Use a recovery code</a> : null}
      </p>
    </section>
  </main>;
}

export function ErrorScreen({ kind }: { kind: string }) {
  const messages: Record<string, [string, string]> = {
    "404": ["We couldn't find that page", "The address does not match a route available in this workspace."],
    "403": ["You don't have access to this", "Your role does not permit this page in the current workspace."],
    "402": ["This capability isn't included in your plan", "Compare plans, or return to the live view without changing your subscription."],
    "500": ["Something went wrong on our side", "Retry the request or check the public status page."],
    "503": ["Precyz is temporarily unavailable", "The affected capability is not available right now."],
  };
  const [title, copy] = messages[kind] ?? messages["404"];
  return <main className="auth"><a href="https://precyz.com" className="brand">Precyz</a>
    <section className="auth-card"><p className="eyebrow">{kind}</p><h1>{title}</h1><p>{copy}</p>
      <div className="actions"><a href="/live">Go to live view</a><a href="https://status.precyz.com">Status page</a></div>
    </section></main>;
}

export function BillingScreen({ plan: currentPlan }: { plan: Plan }) {
  const [prices, setPrices] = useState<Record<string, string>>({});
  useEffect(() => {
    fetch("/v1/billing/prices").then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries<any>(data.plans ?? {})) next[key] = value.display_price;
        setPrices(next);
      }).catch(() => setPrices({}));
  }, []);
  return <section><h1>Plan and billing</h1>
    <p>Your current plan is {currentPlan[0].toUpperCase() + currentPlan.slice(1)}. Prices below come from the billing service.</p>
    <div className="cards">{["solo", "pro", "team", "enterprise"].map(plan =>
      <article className="panel" key={plan}><h2>{plan[0].toUpperCase() + plan.slice(1)}</h2>
        <p className="metric">{prices[plan] ?? "Loading current price…"}</p>
        <a href={`https://precyz.com/plans/${plan}`}>Plan details</a>
      </article>)}</div></section>;
}

export function DevicesScreen() {
  const [devices, setDevices] = useState<Array<Record<string, string>>>([]);
  const [token, setToken] = useState("");
  const [message, setMessage] = useState("");
  const load = () => api<{ devices: Array<Record<string, string>> }>("/v1/devices").then(d => setDevices(d.devices ?? [])).catch(() => setDevices([]));
  useEffect(() => { load(); }, []);
  async function issue() {
    const data = await api<{ token: string; deep_link: string; copy_activation_code: string }>("/v1/devices/install-token", { method: "POST", body: "{}" });
    const code = data.copy_activation_code || data.token;
    setToken(code);
    offerInstallToken(code);
    setMessage("If the extension is installed, it activates automatically. Otherwise paste the code in the Precyz popup. Precyz never rewrites the download to inject the token.");
  }
  return <section><p className="eyebrow">DEVICES</p><h1>Devices</h1>
    <div className="panel"><h2>Browser extension (developer preview)</h2>
      <p>This is a load-unpacked Chrome preview, not a Chrome Web Store listing. Download the zip, unzip it, open chrome://extensions, enable Developer mode, and choose Load unpacked.</p>
      <p><a href={CHROME_DOWNLOAD}>Download the Chrome extension zip</a></p>
    </div>
    <div className="panel"><h2>Activate a client</h2>
      <p>Create a one-time code, then paste it in the extension popup or a desktop client. Precyz never rewrites the download to inject the token.</p>
      <Button type="button" onClick={() => issue().catch(err => setMessage(notice(err)))}>Create activation code</Button>
      {token ? <pre className="codes">{token}</pre> : null}
      {message ? <p>{message}</p> : null}
    </div>
    <div className="panel"><h2>Fleet</h2>
      {devices.length === 0 ? <p>No devices yet. After activation, they appear here without sample data.</p> :
        <ul>{devices.map(d => <li key={d.device_id}><a href={`/devices/${d.device_id}`}>{d.label || d.platform} · {d.state}</a></li>)}</ul>}
    </div>
  </section>;
}

export function ResourceScreen({ path, title, fetchPath, empty }: { path: string; title: string; fetchPath: string; empty: string }) {
  const [rows, setRows] = useState<unknown[]>([]);
  useEffect(() => {
    api<Record<string, unknown>>(fetchPath).then(data => {
      const list = Object.values(data).find(Array.isArray);
      setRows((list as unknown[]) ?? []);
    }).catch(() => setRows([]));
  }, [fetchPath]);
  return <section><p className="eyebrow">{path.split("/")[1]?.toUpperCase()}</p><h1>{title}</h1>
    <div className="panel"><h2>{path.split("/").pop()?.replaceAll("-", " ") || "Overview"}</h2>
      {rows.length === 0 ? <p>{empty}</p> : <pre className="codes">{JSON.stringify(rows, null, 2)}</pre>}
    </div></section>;
}

export function GenericScreen({ section, path }: { section: string; path: string }) {
  if (section === "devices") return <DevicesScreen/>;
  const title = labels[section] ?? "Precyz";
  const fetchers: Record<string, [string, string]> = {
    "/alerts": ["/v1/alerts", "No alerts. Rules fire only on observed destination countries."],
    "/alerts/rules": ["/v1/alert-rules", "No country-difference rules yet."],
    "/reports": ["/v1/reports", "No reports. Generate evidence from retained metadata on a paid plan."],
    "/privacy": ["/v1/privacy/schema", "Closed field allowlist for connection metadata."],
    "/privacy/receipts": ["/v1/privacy/receipts", "No deletion or export receipts."],
    "/org": ["/v1/org", "Organisation scope is empty until members are invited."],
    "/org/members": ["/v1/org/members", "You are the only member until invites are accepted."],
    "/audit": ["/v1/audit", "Hash-chained audit is empty until a state-changing action occurs."],
    "/integrations": ["/v1/integrations", "No destinations. Webhooks carry alert and audit events only."],
    "/developer": ["/v1/pats", "No personal access tokens."],
    "/posture": ["/v1/posture", "Insufficient data until enough observed locations exist. This is never treated as a passing score."],
    "/settings/notifications": ["/v1/notification-preferences", "Choose how Precyz reaches you. Destination attributes are never included."],
    "/assistant": ["/v1/assistant/query", "The assistant answers from metadata summaries, never raw telemetry rows."],
  };
  const spec = fetchers[path];
  if (spec) return <ResourceScreen path={path} title={title} fetchPath={spec[0]} empty={spec[1]}/>;
  return <section><p className="eyebrow">{section.toUpperCase()}</p><h1>{title}</h1>
    <div className="panel"><h2>{path.split("/").pop()?.replaceAll("-", " ") || "Overview"}</h2>
      <p>This workspace shows tenant-scoped records and configuration for {title.toLowerCase()}. Empty states remain explicit; no sample customer data is substituted.</p>
    </div></section>;
}

export function Locked({ section }: { section: string }) {
  const outcomes: Record<string, string> = {
    alerts: "Get notified when observed locations differ from your record",
    reports: "Create dated evidence from retained connection metadata",
    integrations: "Send signed events to destinations you own",
    developer: "Read your retained data through the public API",
  };
  const plan = section === "integrations" ? "Team" : "Pro";
  return <LockState outcome={outcomes[section] ?? "Use this capability"} plan={plan}/>;
}
